import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import {drawColumns, drawGroundPosition,drawGrayGroundPosition, drawRows, getHeight, getWidth, GroundType} from './ground-cell';
import {MapViewComponent} from '../../../shared/map-view/map-view.component';
import {LayoutService} from '../../../core/data-sources/layout.service';
import {MatDialog} from '@angular/material/dialog';
import {Crate, ICrateMap} from '../../../../models/Crate';
import { MapPositionDetailsModalComponent } from 'src/app/modals/map-position-details-modal/map-position-details-modal.component';
import { TaskRequestSidenavService } from 'src/app/core/services/task-request-sidenav.service';
import { Level } from 'src/app/modals/stock-levels-model/stock-levels-modal.component';
import { MapTooltipContent } from '../map-tooltip/map-tooltip.types';
import { TooltipDimensions } from '../map-tooltip/map-tooltip.component';
import { CrateTooltipService } from '../../../core/services/crate-tooltip.service';
import * as PIXI from 'pixi.js';
import { Router } from '@angular/router';

export enum MouseQuadrant {
  UNDEFINED = 0,
  TOP_LEFT = 1,
  TOP_RIGHT = 2,
  BOTTOM_RIGHT = 3,
  BOTTOM_LEFT = 4
}

function getQuadrantName(mouseQuadrant: MouseQuadrant): string {
    switch (mouseQuadrant) {
      case MouseQuadrant.TOP_LEFT:
        return 'TOP_LEFT';
      case MouseQuadrant.TOP_RIGHT:
        return 'TOP_RIGHT';
      case MouseQuadrant.BOTTOM_RIGHT:
        return 'BOTTOM_RIGHT';
      case MouseQuadrant.BOTTOM_LEFT:
        return 'BOTTOM_LEFT';
      default:
        return 'UNDEFINED';
    }
  }

export interface MapGroundPosition {
  rfId: string;
  x: number;
  y: number;
  line: string;
  column: string;
  type: string;
  groupName: string;
  hasPallet: boolean;
  state: string;
  alias?: string;
  groundPositionGroupId?: string;
  createdAt?: number;
  updatedAt?: number;
  levels?: Level[];
  id: string;
}

function calcCanvasMousePosition(canvas: HTMLCanvasElement, mouseX: number, mouseY: number) {
  const canvasTop = canvas.getBoundingClientRect().top;
  const canvasLeft = canvas.getBoundingClientRect().left;
  return {
    canvasMouseX: mouseX - canvasLeft,
    canvasMouseY: mouseY - canvasTop
  }
}
function calcQuadrant(canvas: HTMLCanvasElement, canvasMouseX: number, canvasMouseY: number) {
  const canvasWidth = canvas.getBoundingClientRect().width;
  const canvasHeight = canvas.getBoundingClientRect().height;
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;
  
  let mouseQuadrant: MouseQuadrant = MouseQuadrant.UNDEFINED;

  if (canvasMouseX < centerX && canvasMouseY < centerY) {
    mouseQuadrant = MouseQuadrant.TOP_LEFT;
  } else if (canvasMouseX >= centerX && canvasMouseY < centerY) {
    mouseQuadrant = MouseQuadrant.TOP_RIGHT;
  } else if (canvasMouseX >= centerX && canvasMouseY >= centerY) {
    mouseQuadrant = MouseQuadrant.BOTTOM_RIGHT;
  } else {
    mouseQuadrant = MouseQuadrant.BOTTOM_LEFT;
  }
  return mouseQuadrant;
}

@Component({
  selector: 'app-warehouse-map-task-position-view',
  templateUrl: './warehouse-map-task-position-view.component.html',
  styleUrls: ['./warehouse-map-task-position-view.component.scss']
})
export class WarehouseMapTaskPositionViewComponent extends MapViewComponent implements OnInit, OnChanges {

  public layout;
  public levelsByXY = new Map<string, Level[]>();

  @Input()
  public devicePixelRatio = window.devicePixelRatio || 1;

  @Input()
  public events;

  @Input() map;
  @Input() selectedPositionIds: string[] = [];

  public highlight;
  public mouseX;
  public mouseY;
  public mouseQuadrant: MouseQuadrant = MouseQuadrant.UNDEFINED;
  public showTooltip;
  public tooltipValues: MapTooltipContent = null;
  public isMapEmpty = false;
  public timeout;
  @Output() mapLoaded = new EventEmitter();

  @ViewChild('canvas', { static: false }) canvas: ElementRef;
  @ViewChild('tooltipElement', { static: false }) tooltipElement: ElementRef;

  @Output() toggleTipEvent = new EventEmitter<boolean>(false);
  @Input() openTip = false;
  toggleTip = false;

  // Keep track of tooltip dimensions
  private tooltipWidth = 148;
  private tooltipHeight = 100;

  @HostListener('mousemove', ['$event']) onMouseMove(event) {
    const canvas: HTMLCanvasElement = (this.elementRef.nativeElement as HTMLElement).querySelector('canvas');
    const canvasMousePosition = calcCanvasMousePosition(canvas, event.clientX, event.clientY);
    this.mouseX = canvasMousePosition.canvasMouseX;
    this.mouseY = canvasMousePosition.canvasMouseY;
    this.mouseQuadrant = calcQuadrant(canvas, canvasMousePosition.canvasMouseX, canvasMousePosition.canvasMouseY);
  }

  
  constructor(
    protected elementRef: ElementRef,
    protected ngZone: NgZone,
    private layoutService: LayoutService,
    private dialog: MatDialog,
    private sidenavService: TaskRequestSidenavService,
    private cdr: ChangeDetectorRef,
    private crateTooltipService: CrateTooltipService,
    private router: Router
  ) {
    super(elementRef, ngZone);
  }

  /**
   * A more reliable way to set tooltip position - handles edge cases better
   */
  public getTooltipPosition() {
    const tooltipOffset = 20; // Distance from the cursor
    
    // Make sure we have reasonable dimensions to work with
    if (this.tooltipWidth <= 0) this.tooltipWidth = 150;
    if (this.tooltipHeight <= 0) this.tooltipHeight = 100;
    
    const canvas = (this.elementRef.nativeElement as HTMLElement).querySelector('canvas');
    if (canvas) {
      const canvasRect = canvas.getBoundingClientRect();
      const canvasWidth = canvasRect.width;
      const canvasHeight = canvasRect.height;
      
      // Ensure tooltip stays within canvas bounds
      let leftPos, topPos;
      
      // Position based on quadrant
      switch (this.mouseQuadrant) {
        case MouseQuadrant.TOP_LEFT:
          leftPos = this.mouseX + tooltipOffset;
          topPos = this.mouseY + tooltipOffset;
          break;
        case MouseQuadrant.TOP_RIGHT:
          leftPos = this.mouseX - this.tooltipWidth;
          topPos = this.mouseY + tooltipOffset;
          break;
        case MouseQuadrant.BOTTOM_RIGHT:
          leftPos = this.mouseX - this.tooltipWidth;
          topPos = this.mouseY - this.tooltipHeight;
          break;
        case MouseQuadrant.BOTTOM_LEFT:
          leftPos = this.mouseX + tooltipOffset;
          topPos = this.mouseY - this.tooltipHeight;
          break;
        default:
          leftPos = this.mouseX + tooltipOffset;
          topPos = this.mouseY + tooltipOffset;
      }
      
      // Adjust to keep within boundaries
      if (leftPos < 0) leftPos = 0;
      if (topPos < 0) topPos = 0;
      if (leftPos + this.tooltipWidth > canvasWidth) {
        leftPos = canvasWidth - this.tooltipWidth;
      }
      if (topPos + this.tooltipHeight > canvasHeight) {
        topPos = canvasHeight - this.tooltipHeight;
      }
      
      return {
        'top.px': topPos,
        'left.px': leftPos
      };
    }
    
    // Fallback to simple positioning if canvas not available
    const fallbackPosition = {
      'top.px': this.mouseY + tooltipOffset,
      'left.px': this.mouseX + tooltipOffset
    };
    return fallbackPosition;
  }



  ngOnChanges(changes: SimpleChanges) {
    if (changes.map?.currentValue) {
      if (!changes.map.currentValue?.length) {
        this.isMapEmpty = true;
        this.mapLoaded.emit();
        return;
      }

      const gradePositions = this.getMaxCoordinates(changes.map.currentValue);
      const maxWidth = getWidth(gradePositions.positionsX.length);
      const maxHeight = getHeight(gradePositions.positionsY.length);
      this.processLevels(changes.map.currentValue);

      this.layoutService.getMapCrates().then(res => {
        this.layoutService.cratesCount$.next(res.length);
        this.processProducts(res);
      });


      this.initMap(maxWidth, maxHeight);
      this.drawMap(changes.map.currentValue);
      setTimeout(() => {
        this.mapLoaded.emit();
      }, 300); // 300ms é o tempo médio que termina a renderização do mapa na tela depois do load acabar.
      // TODO: tentar achar um jeito melhor de previnir que o canva pisque preto depois do load acabar.
    }
    if (changes.openTip?.currentValue) {
      this.toggleTip = changes.openTip.currentValue;
    }
    if (changes.selectedPositionIds) {
      this.applyHighlight(changes.selectedPositionIds.currentValue);
    }
  }

  ngOnInit(): void {
   
  }

  public processLevels(groundPositions: MapGroundPosition[]) {
    this.levelsByXY.clear();
    for (const gp of groundPositions) {
      const idx = `${gp.x}_${gp.y}`;
      if (!this.levelsByXY.has(idx)) {
        this.levelsByXY.set(idx, gp.levels || []);
      }
    }
  }

  public processProducts(crates: ICrateMap[]) {
    this.crateTooltipService.processCratesAndLevels(crates as Crate[], this.levelsByXY);
  }

  public getToolTipValues(x: number, y: number, groundPositionType?: string) {
    this.tooltipValues = this.crateTooltipService.getTooltipContent(x, y, groundPositionType);
  }

  public drawMap(groundPositions: MapGroundPosition[]) {
    if (groundPositions) {
      this.drawGrades(groundPositions);
      this.drawDefaultMap(groundPositions);
    }
  }

  drawGrades(groundPositions: MapGroundPosition[]) {
    const gradePositions = this.getMaxCoordinates(groundPositions);
    const columns = drawColumns(gradePositions.positionsX);
    for (const c of columns) {
      c.interactive = true;
      c.cursor = 'pointer';
      c.on('click', () => {
        this.openLevelsModal(null, 'column', c.text);
      });
      // @ts-ignore
      this.viewPort.addChild(c);
    }
    const rows = drawRows(gradePositions.positionsY);
    for (const r of rows) {
      r.interactive = true;
      r.cursor = 'pointer';
      r.on('click', () => {
        this.openLevelsModal(null, 'row', r.text);
      });
      // @ts-ignore
      this.viewPort.addChild(r);
    }
  }

  onTooltipDimensionsChange(dimensions: TooltipDimensions) {
    this.tooltipWidth = dimensions.width;
    this.tooltipHeight = dimensions.height;
    this.cdr.detectChanges();
  }

  openLevelsModal(rfId: string, type: 'row' | 'column' | 'position', position?: string, alias?: string) {
    this.dialog.open(MapPositionDetailsModalComponent, {
      panelClass: 'details-modal',
      data: { rfId, type, position, alias }
    }).afterClosed().subscribe(res => {
      if (res?.openRelocateSidenav) {
        const crate = res.crate;
        const productName = crate.skuGroups[0]?.name ?? 'nome não disponível';
        const data = {
          type: 'STOCK_RELOCATE_SINGLE_PALLET',
          rfId: crate.tagRfid,
          product: productName
        };
        this.sidenavService.toggle(data);
      } else if (res?.openInventorySidenav) {
        const data = {
          type: "STOCK_INVENTORY",
          data: {
            isPicking: false,
            position: res.position
          }
        };
        this.sidenavService.toggle(data);
      } else if (res?.openPalletInventorySidenav) {
        const data = {
          type: "PALLET_INVENTORY",
          data: {
            crate: res.crate, 
            position: res.position
          }
        };
        this.sidenavService.toggle(data);
      }
    });
  }

  drawDefaultMap(groundPositions: MapGroundPosition[]) {
    for (const groundPosition of groundPositions) {
      const elements = drawGroundPosition(groundPosition);
      const area = elements[0];
      area.interactive = true;
      area.cursor = 'pointer';

      area.on('pointerover', () => {
        if (groundPosition.hasPallet){
          this.getToolTipValues(groundPosition.x, groundPosition.y, groundPosition.type);
        } else{
          // no pallet...
          this.tooltipValues = {levels: [], empty: true, isPigeonhole: groundPosition.type === GroundType.PIGEONHOLE};
        }
        if (this.tooltipValues){
          this.showTooltip = true;
          if (this.timeout){
            clearTimeout(this.timeout);
          }
        }
      });
      area.on('click', () => {
        if (groundPosition.type === GroundType.PIGEONHOLE) {
          this.router.navigateByUrl(`map/pigeonhole/${groundPosition.id}`);
          return;
        }
        this.openLevelsModal(groundPosition.rfId, 'position', null, groundPosition.alias);
      });
      area.on('pointerout', () => {
        this.timeout = setTimeout(() => {
          this.showTooltip = false;
          this.tooltipValues = null;
          this.timeout = null;
        }, 50);
      });



      for (const e of elements) {
        // @ts-ignore
        this.viewPort.addChild(e);
      }
    }
  }

  onToggleTip() {
    this.toggleTip = !this.toggleTip;
    this.toggleTipEvent.emit(this.toggleTip);
    this.openTip = false;
  }

  getMaxCoordinates(groundPositions) {
    const positionsX = [];
    const positionsY = [];
    let x = 0;
    let y = 0;
    for (const gp of groundPositions) {
      if (gp.x > x) { x = gp.x; }
      if (gp.y > y) { y = gp.y; }
    }
    let countX = 0;
    while (countX <= x) {
      positionsX.push(countX);
      countX++;
    }
    let countY = 0;
    while (countY <= y) {
      positionsY.push(countY);
      countY++;
    }

    return {positionsX, positionsY};
  }

  public clearHighlight() {
    if (this.highlight) {
      this.highlight.destroy();
      this.highlight = null;
    }
  }

  public applyHighlight(positionRfIds: string[]) {
    if (!this.map) { return; }
    if (!positionRfIds || positionRfIds.length === 0) {
      this.clearHighlight();
      this.clearMapPositions();
      this.drawDefaultMap(this.map);
      return;
    }
    const filteredPositions = this.map.filter(p => positionRfIds.includes(p.rfId));
    this.drawMapWithFilter(this.map, filteredPositions);
  }

  public drawMapWithFilter(allPositions, filteredPositions) {
    this.clearMapPositions();
    
    const filteredIdsSet = new Set(filteredPositions.map(p => p.rfId));
    
    for (const groundPosition of allPositions) {
      let elements;
      
      if (filteredIdsSet.has(groundPosition.rfId)) {
        elements = drawGroundPosition(groundPosition);
      } else {
        elements = drawGrayGroundPosition(groundPosition);
      }
      
      const area = elements[0];
      area.interactive = true;
      area.cursor = 'pointer';

      area.on('pointerover', () => {
        if (groundPosition.hasPallet){
          this.getToolTipValues(groundPosition.x, groundPosition.y);
        } else{
          this.tooltipValues = {levels: [], empty: true};
        }
        if (this.tooltipValues){
          this.showTooltip = true;
          if (this.timeout){
            clearTimeout(this.timeout);
          }
        }
      });
      
      area.on('click', () => {
        this.openLevelsModal(groundPosition.rfId, 'position', null, groundPosition.alias);
      });
      
      area.on('pointerout', () => {
        this.timeout = setTimeout(() => {
          this.showTooltip = false;
          this.tooltipValues = null;
          this.timeout = null;
        }, 50);
      });

      for (const e of elements) {
        // @ts-ignore
        this.viewPort.addChild(e);
      }
    }
  }

  private clearMapPositions() {
    const grades = [];
    // @ts-ignore
    for (const child of this.viewPort.children) {
      if (child instanceof PIXI.Text) {
        grades.push(child);
      }
    }
    
    // @ts-ignore
    this.viewPort.removeChildren();
    
    for (const grade of grades) {
      // @ts-ignore
      this.viewPort.addChild(grade);
    }
    
  }

  @HostListener('window:resize')
  public resize() {
    super.resize();
  }
}